/**
 * Email Verification Banner
 * Shows a banner prompting unverified users to verify their email
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase/config';
import { useAuth } from '../../contexts/AuthContext';

export const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // Don't show banner if user is verified or banner is dismissed
  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const handleResendEmail = async () => {
    setResending(true);
    setResendMessage('');

    try {
      const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');
      const result = await resendVerificationEmail({});
      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setResendMessage('✓ Verification email sent! Check your inbox.');
      } else {
        setResendMessage('⚠ ' + (data.message || 'Failed to send email. Try again later.'));
      }

      // Clear message after 5 seconds
      setTimeout(() => setResendMessage(''), 5000);
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      
      let errorMessage = '⚠ An error occurred. Please try again later.';
      
      if (error.code === 'functions/resource-exhausted') {
        errorMessage = '⚠ ' + (error.message || 'Please wait before requesting another email.');
      } else if (error.message) {
        errorMessage = '⚠ ' + error.message;
      }
      
      setResendMessage(errorMessage);
      
      // Clear message after 5 seconds
      setTimeout(() => setResendMessage(''), 5000);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center flex-1">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-yellow-800">
                <strong>Please verify your email address</strong>
                {' '}— We sent a verification email to{' '}
                <span className="font-semibold">{user.email}</span>
              </p>
              {resendMessage && (
                <p className="text-xs text-yellow-700 mt-1">{resendMessage}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleResendEmail}
              disabled={resending}
              className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resending ? 'Sending...' : 'Resend Email'}
            </button>
            
            <button
              onClick={() => setDismissed(true)}
              className="text-yellow-600 hover:text-yellow-800"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
